/**
 * Phone app offers. One place for the copy, and one decision per platform
 * for whether the web app shows a waitlist opt-in or a store link.
 *
 * The server reads IOS_APP_STORE_URL from GET /api/config/ios-app and
 * ANDROID_PLAY_STORE_URL from GET /api/config/android-app. A usable
 * http(s) URL swaps only that platform. Anything else stays a waitlist.
 */
import { useEffect, useState } from 'react';
import { configAPI } from './api';

export const PHONE_APP_COPY = {
  iosLabel: 'Apple app coming soon. Join wait list.',
  iosJoinedLabel: "You're on the Apple app waitlist.",
  androidLabel: 'Android app coming soon. Join wait list.',
  androidJoinedLabel: "You're on the Android app waitlist.",
  emailNeeded: 'Enter your email so we can add you to the wait list.',
  alreadyHasApp: 'You already have an account and the app',
  updateLink: 'Get the update',
  joinTitle: 'Join the waitlist',
  joinConfirm: 'Join waitlist',
  helper: "We'll let you know when the PeopleiKnow app is ready for your phone.",
  comingSoon: 'PeopleiKnow is optimized for the native app.',
  webNow: 'Sign up via the web app for now until the native app is live.',
  appStoreLabel: 'Get it on the App Store',
  playStoreLabel: 'Get it on Google Play',
  settingsTitle: 'PHONE APPS',
};

/** A waitlist join is confirmed only after an email is already on the form or account. */
export function emailLooksCaptured(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export function safeStoreUrl(value) {
  const url = typeof value === 'string' ? value.trim() : '';
  if (!url) return '';
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return url;
  } catch {
    return '';
  }
}

export function phoneOfferMode(storeUrl) {
  const url = safeStoreUrl(storeUrl);
  if (url) return { mode: 'store', url };
  return { mode: 'waitlist' };
}

export function usePhoneAppOffer() {
  const [state, setState] = useState({ ready: false, appStoreUrl: null, playStoreUrl: null });
  useEffect(() => {
    let live = true;
    const read = (request, key) => request()
      .then(({ data }) => data?.[key] || null)
      .catch(() => null);
    Promise.all([
      read(configAPI.iosApp, 'app_store_url'),
      read(configAPI.androidApp, 'play_store_url'),
    ]).then(([appStoreUrl, playStoreUrl]) => {
      if (live) setState({ ready: true, appStoreUrl, playStoreUrl });
    });
    return () => { live = false; };
  }, []);
  return state;
}
